import { MatterCreateHotkeys } from "@/components/features/matter/overview/MatterCreateHotkeys";
import { MatterActionBar } from "@/components/features/matter/layout/MatterActionBar/MatterActionBar";
import { MatterInformationBanner } from "@/components/features/matter/layout/MatterInformationBanner";
import { MatterNotFound } from "@/components/features/matter/layout/MatterNotFound/MatterNotFound";
import { AppPageShell, GlobalNotAuthorized } from "@/components/ui";
import { GlobalErrorPage } from "@/components/ui/GlobalErrorPage/GlobalErrorPage";
import LawWorksLoader from "@/components/ui/LawWorksLoader/LawWorksLoader";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { useRightPanel } from "@/providers/RightPanelProvider";
import { cn } from "@/lib/utils/cn";
import { ErrorBoundary } from "@sentry/react";
import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Suspense, type ReactNode } from "react";

export const Route = createFileRoute("/_auth/matters/$matterId/_layout")({
  component: RouteComponent,
  pendingComponent: () => (
    <div className="flex w-full">
      <LawWorksLoader className="w-full" />
    </div>
  ),

  errorComponent: () => {
    return <GlobalErrorPage />;
  },
});

function RouteComponent() {
  const { matterId } = Route.useParams();
  const pathname = useLocation({ select: (location) => location.pathname });
  const { currentUser, hasPagePermission } = useUser();
  if (!hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetail)) {
    return <GlobalNotAuthorized />;
  }

  const detailPath = pathname.slice(`/matters/${matterId}`.length);
  const requiredPagePermission = getMatterDetailPagePermission(detailPath);
  if (
    (requiredPagePermission && !hasPagePermission(requiredPagePermission)) ||
    (detailPath.startsWith("/charges") && !currentUser.owner)
  ) {
    return <GlobalNotAuthorized />;
  }
  return (
    <AppPageShell padding="none" className="h-full min-h-0">
      <ErrorBoundary
        key={matterId}
        fallback={({ error }) => (
          <>
            {error instanceof AxiosError && error.response?.status === 404 ? (
              <MatterNotFound />
            ) : (
              <GlobalErrorPage />
            )}
          </>
        )}
      >
        <Suspense fallback={<LawWorksLoader />}>
          <div className="h-full min-h-0">
            <MatterPageWithSidebar matterId={matterId}>
              <Outlet />
            </MatterPageWithSidebar>
          </div>
        </Suspense>
      </ErrorBoundary>
      <MatterCreateHotkeys matterId={matterId} />
    </AppPageShell>
  );
}

function getMatterDetailPagePermission(path: string) {
  if (path === "" || path === "/") {
    return PAGE_PERMISSION_KEYS.mattersDetailOverview;
  }
  if (path.startsWith("/matter-details")) {
    return PAGE_PERMISSION_KEYS.mattersDetailParties;
  }
  if (path.startsWith("/incident-details")) {
    return PAGE_PERMISSION_KEYS.mattersDetailIncidentDetails;
  }
  if (path.startsWith("/custom-fields")) {
    return PAGE_PERMISSION_KEYS.mattersDetailCustomFields;
  }
  if (path.startsWith("/property-damage")) {
    return PAGE_PERMISSION_KEYS.mattersDetailPropertyDamage;
  }
  if (path.startsWith("/emails")) {
    return PAGE_PERMISSION_KEYS.mattersDetailEmails;
  }
  if (path.startsWith("/liens")) {
    return PAGE_PERMISSION_KEYS.mattersDetailLiens;
  }
  if (path.startsWith("/costs")) {
    return PAGE_PERMISSION_KEYS.mattersDetailCosts;
  }
  if (path.startsWith("/medical-treatments")) {
    return PAGE_PERMISSION_KEYS.mattersDetailMedicalTreatments;
  }
  if (path.startsWith("/settlements")) {
    return PAGE_PERMISSION_KEYS.mattersDetailSettlements;
  }
  if (path.startsWith("/litigation")) {
    return PAGE_PERMISSION_KEYS.mattersDetailLitigation;
  }
  if (path.startsWith("/tasks")) {
    return PAGE_PERMISSION_KEYS.mattersDetailTasks;
  }
  if (path.startsWith("/notes")) {
    return PAGE_PERMISSION_KEYS.mattersDetailNotes;
  }
  if (path.startsWith("/updates")) {
    return PAGE_PERMISSION_KEYS.mattersDetailUpdates;
  }
  if (path.startsWith("/files")) {
    return PAGE_PERMISSION_KEYS.mattersDetailFiles;
  }
  if (path.startsWith("/signature-requests")) {
    return PAGE_PERMISSION_KEYS.mattersDetailSignatureRequests;
  }
  return undefined;
}

function MatterPageWithSidebar({
  matterId,
  children,
}: {
  matterId: string;
  children: ReactNode;
}) {
  const { isPanelOpen } = useRightPanel();
  const pathname = useLocation({ select: (location) => location.pathname });
  const isOverview =
    pathname === `/matters/${matterId}` || pathname === `/matters/${matterId}/`;
  const isActionBarExpanded = isPanelOpen("matter-action-bar");
  const { data: matterDetails } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = matterDetails.data;

  if (!matter.id && !matter.id) {
    return children;
  }

  return (
    <div className="relative h-full min-h-0 w-full min-w-0 overflow-hidden">
      <div
        key={pathname}
        className={cn(
          "flex h-full min-h-0 flex-col overflow-hidden transition-[padding] duration-300 ease-out",
          isActionBarExpanded ? "pr-[488px]" : "pr-12",
        )}
      >
        {!isOverview ? (
          <div className="shrink-0">
            <MatterInformationBanner key={matter.id} matter={matter} />
          </div>
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
      <MatterActionBar matter={matter} />
    </div>
  );
}
